import { animated, IntersectionArgs, useInView } from '@react-spring/web';
import React, { PropsWithChildren, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTractionService } from '../../services';
import ProofRequest from '../proof/ProofRequest';
import { FullScreenSpinner } from '../../components/molecules/Spinner';
import { IThemeContext, useCredentialData, useThemeContext } from '../../contexts';
import { useNavigate } from 'react-router-dom';
import { CredentialProofRequest } from '../../types/credentials';
import CardSlashIcon from '../../components/Icons/CardSlashIcon';
import { getResponsivePadding, getFluidFontSize, getDeviceInfo } from '../../helpers/utils';

/**
 * @description Credential proof request list component
 * @return {React.FC}
 */
const CredentialProofRequestList: React.FC<PropsWithChildren> = () => {
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [selectedCredentialIndex, setSelectedCredentialIndex] = useState(0);
  const service = useTractionService();
  const { theme } = useThemeContext();
  const navigate = useNavigate();
  const { proofs, fetchProofs, loading, removeProof } = useCredentialData();
  const deviceInfo = getDeviceInfo();

  const styles = createCredentialProofRequestListStyles(theme);

  // Icon Animation
  const [iconAnim, iconAnimStyle] = useInView(
    () => ({
      from: {
        opacity: 0,
        transform: 'translateY(40px)'
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)'
      }
    }),
    [loading] as IntersectionArgs
  );

  // Text Animation
  const [textAnim, textAnimStyle] = useInView(
    () => ({
      from: {
        opacity: 0,
        transform: 'translateY(40px)'
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
        delay: 100
      }
    }),
    [loading] as IntersectionArgs
  );

  const [buttonAnim, buttonAnimStyle] = useInView(
    () => ({
      from: {
        opacity: 0,
        transform: 'translateY(40px)'
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
        delay: 200
      }
    }),
    [loading] as IntersectionArgs
  );

  const refresh = async () => {
    setLocalLoading(true);
    await fetchProofs();
    setLocalLoading(false);
  };

  const onAccept = async (item: CredentialProofRequest) => {
    setShowSuccess(true);
    await service.sendProofPresentation(
      item.presentationExchangeId,
      item.credentials[selectedCredentialIndex].referentId,
      item.requested_attributes,
      item.requested_predicates
    );
  };

  const onDecline = async (item: CredentialProofRequest) => {
    const proofLength = removeProof(item);
    if (proofLength < 1) {
      navigate('/');
    }
    service.rejectProofRequest(item.presentationExchangeId, 'no thanks');
  };

  // Responsive icon size
  const iconSize = deviceInfo.isMobile ? 100 : deviceInfo.isTablet ? 120 : 140;

  if ((loading && proofs.length < 1) || localLoading) {
    return <FullScreenSpinner text={'Checking for requests...'} />;
  }

  return (
    <>
      <View style={styles.container}>
        {proofs.length === 0 ? (
          <View style={styles.emptyListStyle}>
            <View style={{ flex: 1, justifyContent: 'flex-end' }}>
              <animated.div ref={iconAnim} style={iconAnimStyle}>
                <CardSlashIcon iconWidth={iconSize} iconHeight={iconSize}></CardSlashIcon>
              </animated.div>
            </View>

            <View>
              <animated.div ref={textAnim} style={textAnimStyle}>
                <View style={styles.emptyListTextStyle}>
                  <Text style={styles.emptyListMessageTitleStyle}>No requests</Text>
                  <Text style={styles.emptyListMessageSubtitleStyle}>
                    New proof requests will appear here.
                  </Text>
                </View>
              </animated.div>
            </View>

            <View style={{ flex: 1, justifyContent: 'flex-end', padding: 0, width: '100%' }}>
              <animated.div ref={buttonAnim} style={buttonAnimStyle}>
                <View style={styles.emptyListButtonsContainer}>
                  <Pressable
                    style={[styles.buttonStyle, { marginBottom: getResponsivePadding(8, 9, 10) }]}
                    role="button"
                    onPress={async () => {
                      await refresh();
                    }}>
                    <Text style={styles.buttonText}>Refresh</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.buttonStyle, styles.buttonSecondaryStyle]}
                    role="button"
                    onPress={() => {
                      navigate('/');
                    }}>
                    <Text style={[styles.buttonText, styles.buttonTextSecondary]}>
                      Back to Wallet
                    </Text>
                  </Pressable>
                </View>
              </animated.div>
            </View>
          </View>
        ) : (
          <ScrollView style={styles.listContainer}>
            {proofs.map((item, index) => {
              return (
                <View style={styles.proofItemContainer} key={index}>
                  <ProofRequest
                    selectedCredentialIndex={selectedCredentialIndex}
                    setSelectedCredentialIndex={setSelectedCredentialIndex}
                    proofRequest={item}
                    primaryAction={{
                      title: 'Share',
                      action: () => onAccept(item)
                    }}
                    secondaryAction={{
                      title: 'Reject',
                      action: () => onDecline(item)
                    }}
                    showSuccess={showSuccess}
                  />
                </View>
              );
            })}
          </ScrollView>
        )}
      </View>
    </>
  );
};

export default CredentialProofRequestList;

const createCredentialProofRequestListStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    container: {
      width: '100%',
      height: '100%'
    },
    listContainer: {
      width: '100%'
    },
    proofItemContainer: {
      marginBottom: getResponsivePadding(8, 10, 12)
    },
    emptyListStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: getResponsivePadding(28, 32, 36),
      flex: 1,
      width: '100%',
      height: '100%',
      paddingHorizontal: getResponsivePadding(16, 20, 24)
    },
    emptyListTextStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: getResponsivePadding(8, 10, 12),
      flex: 1,
      width: '100%'
    },
    emptyListMessageTitleStyle: {
      fontSize: getFluidFontSize(20, 22, 24, 18, 28),
      fontWeight: '700',
      textAlign: 'center',
      width: '100%',
      color: theme.textPrimary
    },
    emptyListMessageSubtitleStyle: {
      fontSize: getFluidFontSize(14, 16, 18, 12, 20),
      color: theme.textSecondary,
      textAlign: 'center',
      width: '100%',
      paddingHorizontal: getResponsivePadding(8, 12, 16)
    },
    buttonStyle: {
      paddingVertical: getResponsivePadding(12, 14, 16),
      paddingHorizontal: getResponsivePadding(20, 22, 24),
      borderRadius: 6,
      backgroundColor: theme.primary,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)',
      width: '100%'
    },
    buttonText: {
      textAlign: 'center',
      color: 'inherit',
      fontSize: getFluidFontSize(14, 15, 16, 12, 18),
      fontWeight: '600',
      width: '100%'
    },
    buttonSecondaryStyle: {
      backgroundColor: 'white',
      borderColor: 'Black',
      borderWidth: 1
    },
    buttonTextSecondary: {
      color: 'Black'
    },
    emptyListButtonsContainer: {
      width: '100%',
      alignSelf: 'center'
    },
    proofsContainer: {}
  });
